import Link, { LinkProps } from 'next/link';
import React, { useCallback, useMemo } from 'react';
import { useBlockingRouter } from '../../hooks/useBlockingRouter';

export type BlockingLinkProps = LinkProps &
	Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> & {
		children?: React.ReactNode;
	};

export const BlockingLink = React.forwardRef<
	HTMLAnchorElement,
	BlockingLinkProps
>( ( { onClick, href, replace, scroll, ...props }, ref ) => {
	const router = useBlockingRouter();
	const normalizedHref = useMemo( () => {
		if ( typeof href === 'string' ) {
			return href;
		}

		const nextPathname = href.pathname || '';
		const hash = href.hash || '';
		const query = href.query
			? new URLSearchParams(
				Object.entries( href.query ).flatMap( ( [ key, value ] ) => {
					if ( value === undefined ) {
						return [];
					}

					if ( Array.isArray( value ) ) {
						return value.map( ( item ) => [ key, `${ item }` ] );
					}

					return [ [ key, `${ value }` ] ];
				} )
			  ).toString()
			: '';

		return `${ nextPathname }${ query ? `?${ query }` : '' }${ hash }`;
	}, [ href ] );

	const handleClick = useCallback<React.MouseEventHandler<HTMLAnchorElement>>(
		async ( e ) => {
			onClick?.( e );

			if ( e.defaultPrevented ) {
				return;
			}

			// Let modified clicks (new tab, etc.) through without interception.
			if (
				e.button !== 0 ||
				e.metaKey ||
				e.ctrlKey ||
				e.shiftKey ||
				e.altKey ||
				( props.target && props.target !== '_self' ) ||
				e.currentTarget.hasAttribute( 'download' ) ||
				new URL( e.currentTarget.href ).origin !== window.location.origin
			) {
				return;
			}

			e.preventDefault();

			if ( replace ) {
				router.replace( normalizedHref, { scroll } );
				return;
			}

			await router.push( href, { scroll } );
		},
		[
			onClick,
			href,
			normalizedHref,
			replace,
			scroll,
			props.target,
			router
		]
	);

	return (
		<Link
			href={ href }
			replace={ replace }
			scroll={ scroll }
			ref={ ref }
			{ ...props }
			onClick={ handleClick }
		/>
	);
} );
